import { useState } from 'react';

import { Flex, Search2LineIcon, Select } from 'ui';
import { useSearchDebounce } from 'common/hooks';

import { useGlobalSearchInputQuery, useRecentSearchesQuery } from '../queries';
import { RecentSearchesPanel } from './recent-searches-panel';
import { SearchResultsPanel } from './search-results-panel';

type P = {
  onNavigate: (path: string) => void;
};

const GlobalSearchInput = ({ onNavigate }: P) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { isDebouncing, searchDebounced, setSearch } = useSearchDebounce({ delay: 1000 });
  const { data: searchResults = {}, isFetching: isLoadingSearchResults } = useGlobalSearchInputQuery({
    filter: { query: searchDebounced },
  });
  const { data: recentSearches = [], isFetching: isLoadingRecentSearches } = useRecentSearchesQuery(!searchValue);

  const isLoading = isLoadingSearchResults || isLoadingRecentSearches;

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setSearch(value);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const clearSearch = () => {
    setSearchValue('');
    setSearch('');
  };

  const handleNavigate = (path: string) => {
    setIsOpen(false);
    setIsFocused(false);
    onNavigate(path);
  };

  return (
    <>
      <Flex className="global-search-input__wrapper">
        <Select
          allowClear
          className="global-search-input"
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement ?? document.body}
          loading={isLoading || isDebouncing}
          onBlur={handleBlur}
          onClear={clearSearch}
          onFocus={handleFocus}
          onOpenChange={setIsOpen}
          onSearch={handleSearch}
          open={isOpen}
          placeholder="Search user or content..."
          popupClassName="global-search-input__dropdown"
          popupRender={() =>
            searchValue ? (
              <SearchResultsPanel
                clearSearch={clearSearch}
                isLoading={isLoadingSearchResults || isDebouncing}
                onNavigate={handleNavigate}
                searchResults={searchResults}
                searchValue={searchValue}
              />
            ) : (
              <RecentSearchesPanel
                clearSearch={clearSearch}
                isLoading={isLoadingRecentSearches}
                onNavigate={handleNavigate}
                recentSearches={recentSearches}
              />
            )
          }
          prefix={<Search2LineIcon className="global-search-input__icon" />}
          searchValue={searchValue}
          showSearch
          suffixIcon={null}
        />
      </Flex>
      {isFocused ? <div className="global-search-input__overlay" /> : null}
    </>
  );
};

export default GlobalSearchInput;
